import { memo, useCallback, useMemo } from 'react';
import { DateTimePicker } from '@/components/DateTimePicker';
import type { FormSearchPredicateData } from '@/services/api-types';
import type { FormFilterBaseProps } from './filter-base';

export interface DateFilterProps extends FormFilterBaseProps {}

interface DateRange {
  from: string;
  to: string;
}

/** ISO YYYY-MM-DD ↔ Date (UTC midnight) round-trip. Mirrors the DateField in
 *  the form renderer so that DATE values stay timezone-stable. */
function isoToDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}
function dateToIso(d: Date | null): string {
  if (!d) return '';
  return d.toISOString().slice(0, 10);
}

function readRange(predicates: FormSearchPredicateData[]): DateRange {
  const p = predicates[0];
  if (!p) return { from: '', to: '' };
  if (p.op === 'BETWEEN') {
    return {
      from: typeof p.value === 'string' ? p.value : '',
      to: typeof p.value2 === 'string' ? p.value2 : '',
    };
  }
  if (p.op === 'GTE') return { from: typeof p.value === 'string' ? p.value : '', to: '' };
  if (p.op === 'LTE') return { from: '', to: typeof p.value === 'string' ? p.value : '' };
  return { from: '', to: '' };
}

function rangeToPredicates(
  fieldKey: string,
  from: string,
  to: string,
): FormSearchPredicateData[] {
  if (from && to) return [{ fieldKey, op: 'BETWEEN', value: from, value2: to }];
  if (from) return [{ fieldKey, op: 'GTE', value: from }];
  if (to) return [{ fieldKey, op: 'LTE', value: to }];
  return [];
}

/**
 * Two DateTimePicker pickers (from/to) inside a single Records Search `.field`
 * row. Uses the project's standard date picker (not native `<input type="date">`)
 * so the look matches every other date field in the app — and to keep DATE
 * values UTC-anchored, matching how the form renderer stores them.
 */
export const DateFilter = memo(function DateFilter({
  field,
  predicates,
  onPredicatesChange,
}: DateFilterProps) {
  const { from, to } = readRange(predicates);

  const fromDate = useMemo(() => isoToDate(from), [from]);
  const toDate = useMemo(() => isoToDate(to), [to]);

  const handleFromChange = useCallback(
    (next: Date | null) => {
      onPredicatesChange(rangeToPredicates(field.key, dateToIso(next), to));
    },
    [field.key, to, onPredicatesChange],
  );
  const handleToChange = useCallback(
    (next: Date | null) => {
      onPredicatesChange(rangeToPredicates(field.key, from, dateToIso(next)));
    },
    [field.key, from, onPredicatesChange],
  );

  return (
    <div className="field">
      <label>{field.label}</label>
      <div className="form-filter-range">
        <DateTimePicker
          value={fromDate}
          type="date"
          clearable
          appendToBody
          onChange={handleFromChange}
          inputAttr={{ 'aria-label': `${field.label} from` }}
        />
        <span className="form-filter-range__sep">to</span>
        <DateTimePicker
          value={toDate}
          type="date"
          clearable
          appendToBody
          onChange={handleToChange}
          inputAttr={{ 'aria-label': `${field.label} to` }}
        />
      </div>
    </div>
  );
});
